import { DEFAULT_BRANDING, DEFAULT_UI_LABELS, PartnerBranding, UiLabelKey } from "../config/branding";
import { getActingPartner } from "./actingPartner";
import { isPartnerHost } from "./embedHost";
import { hostTenant } from "../branding/generated/brand-manifest";
import { applyThemeToDocument, themeFromWire } from "./partnerTheme";

export type { PartnerBranding, UiLabelKey } from "../config/branding";
export { DEFAULT_BRANDING } from "../config/branding";

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

export const parseHex = (value: string): [number, number, number] | null => {
  const raw = String(value || "").trim();
  if (!HEX.test(raw)) return null;
  let hex = raw.replace("#", "");
  if (hex.length === 3)
    hex = hex
      .split("")
      .map((c) => c + c)
      .join("");
  return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
};

const toHex = (rgb: [number, number, number]): string =>
  "#" +
  rgb
    .map((n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase();

export const mix = (color: string, amount: number): string => {
  const rgb = parseHex(color);
  if (!rgb) return color;
  const target = amount >= 0 ? 255 : 0;
  const t = Math.abs(amount);
  return toHex(rgb.map((c) => c + (target - c) * t) as [number, number, number]);
};

// A brand pack (src/branding/tenants/<id>, picked by domain or ?partner=) supplies the
// app's ASSETS (wordmark, favicon, mark, icons) and FONTS. A partner session inside it
// keeps the pack's whole look, colours included, until the partner's Theme Studio leaves
// the Zenvoya preset (a new partner has no theme, or the Zenvoya one); from then on the
// colours, theme and app labels are the partner's. The partner's own logos, icon,
// favicon and display name are never used under a pack.
const DEFAULT_THEME_PRESET = "zenvoya";

const themeTakesOverPack = (theme: PartnerBranding["theme"]): boolean =>
  Boolean(theme?.preset) && theme?.preset !== DEFAULT_THEME_PRESET;

const withFallbacks = (branding: Partial<PartnerBranding> | null | undefined): PartnerBranding => {
  const pack = Boolean(hostTenant);
  const packColours = pack && !themeTakesOverPack(branding?.theme);
  const colour = (value: string | undefined, fallback: string) =>
    !packColours && parseHex(value || "") ? (value as string) : fallback;
  return {
    code: branding?.code || DEFAULT_BRANDING.code,
    name: (!pack && branding?.name) || DEFAULT_BRANDING.name,
    logoUrl: (!pack && branding?.logoUrl) || DEFAULT_BRANDING.logoUrl,
    logoDarkUrl: (!pack && branding?.logoDarkUrl) || undefined,
    iconUrl: (!pack && branding?.iconUrl) || undefined,
    faviconUrl: (!pack && branding?.faviconUrl) || DEFAULT_BRANDING.faviconUrl,
    primaryColor: colour(branding?.primaryColor, DEFAULT_BRANDING.primaryColor),
    secondaryColor: colour(branding?.secondaryColor, DEFAULT_BRANDING.secondaryColor),
    accentColor: colour(branding?.accentColor, DEFAULT_BRANDING.accentColor),
    labels: branding?.labels || undefined,
    theme: branding?.theme || undefined,
  };
};

/** Known keys with non-empty text only; undefined when the partner renamed nothing. */
const labelsFromWire = (raw: unknown): PartnerBranding["labels"] => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const out: Partial<Record<UiLabelKey, string>> = {};
  for (const key of Object.keys(DEFAULT_UI_LABELS) as UiLabelKey[]) {
    const text = (raw as Record<string, unknown>)[key];
    if (typeof text === "string" && text.trim()) out[key] = text.trim();
  }
  return Object.keys(out).length ? out : undefined;
};

/** The partner's wording for one app label, else the app's own. */
export const brandLabel = (branding: Pick<PartnerBranding, "labels"> | null | undefined, key: UiLabelKey): string =>
  branding?.labels?.[key] || DEFAULT_UI_LABELS[key];

export const brandingFromWire = (payload: any): Partial<PartnerBranding> => {
  const src = payload?.data ?? payload?.theme ?? payload ?? {};
  const out: Record<string, string> = {};
  const set = (key: string, value: unknown) => {
    const v = typeof value === "string" ? value.trim() : value;
    if (v !== undefined && v !== null && v !== "" && v !== "null") out[key] = String(v);
  };

  set("name", src.name ?? src.partnerName);
  set("primaryColor", src.primaryColor);
  set("secondaryColor", src.secondaryColor);
  set("accentColor", src.accentColor ?? src.headerBg ?? src.headerBackgroundColor);

  const logos = src.logos ?? payload?.logos ?? {};
  set("logoUrl", logos.primary?.url ?? src.logoUrl);
  set("logoDarkUrl", logos.dark?.url ?? src.logoDarkUrl);
  set("iconUrl", logos.icon?.url ?? src.iconUrl);
  set("faviconUrl", logos.favicon?.url ?? src.faviconUrl);

  const labels = labelsFromWire(src.uiLabels);
  const theme = themeFromWire(src.resolvedTheme);
  return { ...out, ...(labels ? { labels } : {}), ...(theme ? { theme } : {}) } as Partial<PartnerBranding>;
};

let inFlight: { code: string; token: string; promise: Promise<PartnerBranding> } | null = null;
let resolvedFor: { code: string; branding: PartnerBranding; refusedToken?: string } | null = null;

const memoHit = (code: string, token: string): boolean =>
  resolvedFor?.code === code && (resolvedFor.refusedToken === undefined || resolvedFor.refusedToken === token);

const brandingWarn = (what: string, detail?: unknown): void => {
  if (import.meta.env.MODE === "production") return;
  // eslint-disable-next-line no-console
  console.warn(`[branding] ${what}`, detail);
};

const serverAnswered = (error: unknown): boolean =>
  Boolean((error as { response?: unknown })?.response);

const noAnswer = (code: string, token: string): PartnerBranding | Promise<PartnerBranding> => {
  if (resolvedFor?.code === code && resolvedFor.refusedToken === undefined) return resolvedFor.branding;
  if (inFlight?.code === code && inFlight.token !== token) return inFlight.promise;
  return readCachedBranding(code) ?? DEFAULT_BRANDING;
};

const fetchPartnerBranding = async (code: string, accessJWT?: string | null): Promise<PartnerBranding> => {
  const token = String(accessJWT ?? "");
  try {
    const { getPartnerBrandingApi } = await import("../api/partnerBrandingApi");
    const response = await getPartnerBrandingApi(token, code);
    if (response == null) {
      brandingWarn('view not sent (no usable token) — keeping what is on screen', { code });
      return await noAnswer(code, token);
    }
    const mapped = brandingFromWire(response);
    if (!Object.keys(mapped).length) {
      brandingWarn('view has nothing saved — using the default', { code });
      resolvedFor = { code, branding: DEFAULT_BRANDING };
      return DEFAULT_BRANDING;
    }
    const resolved = withFallbacks({ ...mapped, code });
    if (typeof window !== "undefined") cacheBranding(resolved, code);
    resolvedFor = { code, branding: resolved };
    return resolved;
  } catch (error) {
    if (serverAnswered(error) && inFlight?.code === code && inFlight.token !== token) {
      brandingWarn('view refused for a superseded token — deferring to the newer request', { code });
      return await inFlight.promise;
    }
    if (serverAnswered(error)) {
      brandingWarn('view refused — clearing the cache and resetting to default', {
        code,
        status: (error as { response?: { status?: number } })?.response?.status,
      });
      clearCachedBranding();
      resolvedFor = { code, branding: DEFAULT_BRANDING, refusedToken: token };
      return DEFAULT_BRANDING;
    }
    brandingWarn('view could not be reached — keeping what is on screen', { code });
    return await noAnswer(code, token);
  }
};

export const adoptPartnerBranding = (wire: unknown, partnerCode?: string | null): PartnerBranding => {
  const code = String(partnerCode ?? "").trim();
  if (!code) return DEFAULT_BRANDING;

  const mapped = brandingFromWire(wire);
  if (!Object.keys(mapped).length) {
    resolvedFor = { code, branding: DEFAULT_BRANDING };
    return DEFAULT_BRANDING;
  }

  const resolved = withFallbacks({ ...mapped, code });
  if (typeof window !== "undefined") cacheBranding(resolved, code);
  resolvedFor = { code, branding: resolved };
  return resolved;
};

export const resolvePartnerBranding = async (
  partnerCode?: string | null,
  accessJWT?: string | null
): Promise<PartnerBranding> => {
  const code = String(partnerCode ?? "").trim();

  if (!code) {
    inFlight = null;
    resolvedFor = null;
    return DEFAULT_BRANDING;
  }

  const token = String(accessJWT ?? "");
  if (memoHit(code, token)) return resolvedFor!.branding;

  if (getActingPartner()?.source === "ticket") {
    const cached = readCachedBranding(code);
    if (cached) {
      resolvedFor = { code, branding: cached };
      return cached;
    }
    return DEFAULT_BRANDING;
  }

  if (inFlight?.code === code && inFlight.token === token) return inFlight.promise;

  const promise: Promise<PartnerBranding> = fetchPartnerBranding(code, accessJWT).finally(() => {
    if (inFlight?.promise === promise) inFlight = null;
  });
  inFlight = { code, token, promise };
  return promise;
};

export type BrandColorStep = "primary" | "primary-light" | "primary-light-2" | "primary-dark" | "primary-dark-2" | "secondary" | "accent";

const PARTNER_TOKEN_ROLES: Record<BrandColorStep, string[]> = {
  primary: [
    "primary-main",
    // Only a partner's brand sets this one, so a site whose shipped colour is a
    // fixed purple (not --primary-main) can still follow the partner.
    "zv-brand-primary",
    "avatar-text",
    "text-primary-main",
    "action-selected-text",
    "action-focus-text",
    "btn-active-background",
    "btn-link",
    "sel-con-active-stroke",
    "sel-con-selected-background",
    "sel-con-toggle-selected",
    "other-two-tone-icon-dark",
    "offer-card-icon",
  ],
  "primary-light": ["secondary-main", "secondary-medium", "text-alice-blue", "action-selected-background"],
  "primary-light-2": ["primary-light-2", "action-active-stroke", "action-selected-stroke", "other-two-tone-icon-light"],
  "primary-dark": ["primary-dark"],
  "primary-dark-2": ["primary-dark-2"],
  secondary: ["accent-main", "avatar-bg", "text-accent-main"],
  accent: ["secondary-bright"],
};

const BRAND_COLOR_STEPS = Object.keys(PARTNER_TOKEN_ROLES) as BrandColorStep[];

const partnerPalette = (b: PartnerBranding): Record<BrandColorStep, string> => ({
  primary: b.primaryColor,
  "primary-light": mix(b.primaryColor, 0.88),
  "primary-light-2": mix(b.primaryColor, 0.55),
  "primary-dark": mix(b.primaryColor, -0.3),
  "primary-dark-2": mix(b.primaryColor, -0.58),
  secondary: b.secondaryColor,
  accent: b.accentColor,
});

export const isPartnerBranding = (branding: Pick<PartnerBranding, "code"> | null | undefined): boolean =>
  Boolean(branding?.code);

export const readBrandColor = (step: BrandColorStep, fallback: string): string => {
  if (typeof document === "undefined") return fallback;
  const value = document.documentElement.style.getPropertyValue(`--${PARTNER_TOKEN_ROLES[step][0]}`).trim();
  return value || fallback;
};

const CACHE_KEY = "_zenvoya_branding_cache";

const cacheBranding = (branding: PartnerBranding, code: string): void => {
  try {
    window.sessionStorage.setItem(CACHE_KEY, JSON.stringify({ code, branding }));
  } catch {
  }
};

const clearCachedBranding = (): void => {
  try {
    window.sessionStorage.removeItem(CACHE_KEY);
  } catch {
  }
};

const readCachedBranding = (code: string): PartnerBranding | null => {
  try {
    const raw = window.sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (String(parsed?.code ?? "") !== String(code ?? "")) return null;
    return parsed?.branding?.primaryColor ? (parsed.branding as PartnerBranding) : null;
  } catch {
    return null;
  }
};

export const getBootBranding = (): PartnerBranding => {
  if (typeof window === "undefined") return DEFAULT_BRANDING;
  const code = brandableActingCode();
  if (!code) return DEFAULT_BRANDING;
  return readCachedBranding(code) ?? DEFAULT_BRANDING;
};

const isInPortalFrame = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

const brandableActingCode = (): string => {
  const acting = getActingPartner();

  if (acting?.source === "ticket") return acting.partnerCode || "";

  if (!isInPortalFrame()) return "";
  if (!isPartnerHost()) return "";
  return acting?.partnerCode || "";
};

// A partner session inside a brand pack (a tenant picked by domain or ?partner=) is
// branded too: the pack still supplies the assets and fonts (see withFallbacks and the
// `data-zv-partner-skin` gate in _brand-css-vars.scss), the partner everything else.
// With no partner session DEFAULT_BRANDING already IS that pack.
export const currentBrandingCode = (): string => {
  if (typeof window === "undefined") return "";
  return brandableActingCode();
};

export const applyBrandingAtBoot = (): void => {
  applyBrandingToDocument(getBootBranding());
};

/**
 * Theme Studio live preview (CRM Branding page -> its embedded app frame). While
 * set it wins over whatever branding the app resolves, because the frame's own
 * identity handshake resolves (and re-applies) branding after the preview lands.
 */
let previewBranding: PartnerBranding | null = null;

export const setBrandingPreview = (wire: unknown): PartnerBranding | null => {
  previewBranding = wire ? withFallbacks({ ...brandingFromWire(wire), code: "preview" }) : null;
  return previewBranding;
};

/** The Theme Studio preview while one is active, else null. */
export const getBrandingPreview = (): PartnerBranding | null => previewBranding;

// Set while a partner's Theme Studio is painted over a brand pack: _brand-css-vars.scss then
// stops the pack's own colour tokens, so only the pack's assets and fonts remain.
const PARTNER_SKIN_ATTR = "data-zv-partner-skin";

export const applyBrandingToDocument = (resolved: PartnerBranding): void => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const branding = previewBranding ?? resolved;
  applyThemeToDocument(isPartnerBranding(branding) ? branding.theme : null);

  // Under a pack, a partner still on the Zenvoya preset keeps the pack's colours (see withFallbacks).
  if (!isPartnerBranding(branding) || (hostTenant && !themeTakesOverPack(branding.theme))) {
    root.removeAttribute(PARTNER_SKIN_ATTR);
    for (const step of BRAND_COLOR_STEPS) {
      for (const name of PARTNER_TOKEN_ROLES[step]) {
        root.style.removeProperty(`--${name}`);
        root.style.removeProperty(`--${name}-rgb`);
      }
    }
    return;
  }

  if (hostTenant) root.setAttribute(PARTNER_SKIN_ATTR, "1");
  const b = withFallbacks(branding);
  const palette = partnerPalette(b);
  for (const step of BRAND_COLOR_STEPS) {
    const hex = palette[step];
    const rgb = parseHex(hex);
    for (const name of PARTNER_TOKEN_ROLES[step]) {
      root.style.setProperty(`--${name}`, hex);
      if (rgb) root.style.setProperty(`--${name}-rgb`, rgb.join(", "));
    }
  }

  if (!b.faviconUrl) return;
  const link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
  if (link && link.getAttribute("href") !== b.faviconUrl) link.setAttribute("href", b.faviconUrl);
};
